import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge } from '@/design-system/components/Badge'
import { IconDetail, icons } from '@/design-system/components/IconDetail'
import ProjectFollowButton from '@/components/ProjectFollowButton'
import ReservationCountdown from '@/components/ReservationCountdown'

function ConstructionProgress({ progress = 0, milestone }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-neutral-700 font-medium">Construction Progress</span>
        <span className="text-primary-600 font-semibold">{Math.round(progress)}%</span>
      </div>
      <div className="relative w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-700 ease-out"
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>
      {milestone && (
        <p className="text-xs text-neutral-600 flex items-center gap-1.5">
          {icons.construction}
          <span>{milestone}</span>
        </p>
      )}
    </div>
  )
}

function ImageGallery({ images, projectName }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const validImages = images?.filter(Boolean) || []
  const hasImages = validImages.length > 0

  if (!hasImages) {
    return (
      <div className="w-full aspect-[16/9] bg-gradient-to-br from-neutral-100 to-neutral-200 flex items-center justify-center">
        <svg className="w-16 h-16 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      </div>
    )
  }

  return (
    <div className="relative w-full aspect-[16/9] bg-neutral-900 overflow-hidden group">
      <img
        src={validImages[currentIndex]}
        alt={`${projectName} - Image ${currentIndex + 1}`}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
      />

      {validImages.length > 1 && (
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
            {validImages.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setCurrentIndex(index)
                }}
                className={`h-1.5 rounded-full transition-all touch-target-large ${
                  index === currentIndex
                    ? 'bg-white w-8'
                    : 'bg-white/60 hover:bg-white/80 w-1.5'
                }`}
                aria-label={`View image ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export function ProjectHeroCard({
  project,
  reservation,
  asp,
  onUnfollow,
  onProceedToContract,
  onWithdraw,
  onViewAgreement,
  onViewDiscount,
}) {
  const isReserved = !!reservation && reservation.status === 'active'
  const hasDiscount = reservation?.reservation_discounts?.length > 0

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('a[href]')) {
      return
    }
    window.location.href = `/projects/${project.id}`
  }

  const discountAmount = hasDiscount
    ? parseFloat(reservation.reservation_discounts[0].locked_dollar_amount)
    : 0

  const getCurrentStage = () => {
    if (!reservation) return null
    if (asp) {
      if (['fully_executed'].includes(asp.status)) return 'unconditional'
      if (['draft', 'sent_to_lawyer', 'ready_to_sign', 'signed_by_buyer', 'signed_by_developer'].includes(asp.status)) {
        return 'under_contract'
      }
    }
    return 'reserved'
  }

  const stage = getCurrentStage()

  return (
    <div
      onClick={handleCardClick}
      className="bg-white overflow-hidden transition-all cursor-pointer border-b last:border-b-0 md:border-b-0 md:rounded-2xl md:border md:border-neutral-200 md:shadow-sm md:hover:shadow-lg"
    >
      <div className="relative">
        <ImageGallery images={project.image_urls} projectName={project.name} />

        <div className="absolute top-4 right-4 z-10">
          <ProjectFollowButton
            isFollowing={true}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onUnfollow(project.id)
            }}
            size="md"
          />
        </div>

        {isReserved && (
          <div className="absolute top-4 left-4">
            <Badge variant="info" size="md" className="shadow-lg">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {stage === 'unconditional' ? 'Unconditional' : stage === 'under_contract' ? 'Under Contract' : 'Reserved'}
            </Badge>
          </div>
        )}
      </div>

      <div className="p-5 md:p-6 space-y-4">
        <div>
          <Link
            to={`/projects/${project.id}`}
            className="block group"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl md:text-2xl font-semibold text-neutral-900 mb-2 group-hover:text-primary-600 transition-colors">
              {project.name}
            </h3>
          </Link>

          <IconDetail
            icon={icons.location}
            label={project.location}
            className="text-neutral-600"
          />

          {project.price_range_min && project.price_range_max && !isReserved && (
            <div className="mt-3">
              <p className="text-lg font-semibold text-primary-600">
                ${project.price_range_min.toLocaleString()} - ${project.price_range_max.toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {isReserved && reservation && (
          <div className="pt-4 border-t border-neutral-100 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-neutral-600 mb-1">Your Unit</p>
                <p className="font-semibold text-neutral-900 text-lg">
                  Unit {reservation.unit?.unit_number}
                </p>
                <div className="flex flex-wrap gap-3 mt-2">
                  <IconDetail icon={icons.bed} label={`${reservation.unit?.bedrooms} bed`} />
                  <IconDetail icon={icons.bath} label={`${reservation.unit?.bathrooms} bath`} />
                  <IconDetail icon={icons.area} label={`${reservation.unit?.sqm} sqm`} />
                </div>
              </div>

              <div className="text-right">
                <p className="text-sm text-neutral-600 mb-1">Price</p>
                {hasDiscount ? (
                  <div className="space-y-1">
                    <p className="text-xl font-bold text-success-600">
                      ${(reservation.unit?.price - discountAmount).toLocaleString()}
                    </p>
                    <p className="text-xs text-neutral-400 line-through">
                      ${reservation.unit?.price.toLocaleString()}
                    </p>
                  </div>
                ) : (
                  <p className="text-xl font-bold text-neutral-900">
                    ${reservation.unit?.price?.toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            {hasDiscount && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onViewDiscount(reservation)
                }}
                className="w-full flex items-center justify-between px-4 py-3 bg-success-50 border border-success-200 rounded-xl hover:bg-success-100 transition-colors"
              >
                <span className="text-sm font-medium text-success-800">
                  {icons.check} Discount Applied
                </span>
                <span className="text-lg font-bold text-success-700">
                  Save ${discountAmount.toLocaleString()}
                </span>
              </button>
            )}

            <ReservationCountdown
              expiresAt={reservation.expires_at}
              isReducedDuration={reservation.is_reduced_duration}
            />

            <ConstructionProgress
              progress={40}
              milestone="Foundations complete"
            />

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              {!reservation.asp_initiated_at ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onProceedToContract(reservation)
                  }}
                  className="flex-1 px-5 py-3 bg-gradient-to-r from-success-600 to-success-700 text-white rounded-xl hover:from-success-700 hover:to-success-800 font-semibold text-base flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow-md"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Continue to Contract
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onViewAgreement(reservation)
                  }}
                  className="flex-1 px-5 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-semibold text-base flex items-center justify-center gap-2 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  View Agreement
                </button>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onWithdraw(reservation)
                }}
                className="sm:w-auto px-5 py-3 text-error-600 border-2 border-error-200 rounded-xl hover:bg-error-50 font-medium text-base flex items-center justify-center gap-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="sm:inline">Withdraw</span>
              </button>
            </div>
          </div>
        )}

        {!isReserved && (
          <div className="pt-4 border-t border-neutral-100">
            <Link
              to={`/projects/${project.id}`}
              className="w-full block px-5 py-3 bg-primary-600 text-white text-center rounded-xl hover:bg-primary-700 font-semibold transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              View Project Details
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
